<template>
    <div v-if="isInitiated" class="flex flex-1 w-full h-full">

    <!-- Scanning Mode -->
    <div v-if="isScanning" class="w-full h-full relative" style="min-height: calc((var(--vh) * 100) - 80px)">
      <!-- Video Element -->
      <video 
        ref="videoRef"
        class="absolute inset-0 w-full h-full object-cover"
      ></video>
      
      <!-- Hidden Canvas for QR Processing -->
      <canvas 
        ref="canvasRef" 
        style="display: none"
      ></canvas>
      
      <!-- Stop Scanning Button -->
      <div class="absolute bottom-12 left-0 right-0 flex justify-center z-10">
        <button 
          @click="stopScanning"
          :style="{ backgroundColor: tourSettings.page_action_color, color: tourSettings.page_action_text_color }"
          class="py-2 px-6 rounded-full shadow-md font-medium border-none cursor-pointer"
        >
          {{ $t('camera.stop_scanning') }}
        </button>
      </div>
    </div>
    
    <!-- Start Scanning Mode -->
    <div v-else class="flex flex-col justify-center items-center h-full w-full px-4 py-24 text-center">
      <!-- Error Message -->
      <p v-if="errorMessage" class="text-red-600 mb-4 whitespace-pre-line">{{ $t(errorMessage) }}</p>
      
      <!-- Description -->
      <p v-else class="text-black mb-8">{{ $t('camera.scanner_description') }}</p>
      
      <!-- Start Scanning Button -->
      <button 
        @click="startScanning"
        :style="{ backgroundColor: tourSettings.page_action_color, color: tourSettings.page_action_text_color }"
        class="py-2 px-6 rounded-full shadow-md font-medium border-none cursor-pointer mx-auto"
      >
        {{ $t('camera.start_scanning') }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import jsQR from 'jsqr'

export default {
  name: 'QRCodeScanner',
  setup() {
    const store = useStore()
    const router = useRouter()
    
    // Reactive state
    const isInitiated = ref(false)
    const isScanning = ref(false)
    const errorMessage = ref('')
    const videoRef = ref(null)
    const canvasRef = ref(null)
    let stream = null
    let animationFrameId = null
    
    // Get tour settings from store
    const tourSettings = store.getters['settings/tourSettings']
    
    // QR Code scanning function
    const scan = () => {
      if (!isScanning.value) return
      
      const videoElement = videoRef.value
      const canvasElement = canvasRef.value
      
      // Check if elements exist before using them
      if (!videoElement || !canvasElement) {
        console.warn('Video or canvas element not available for scanning')
        return
      }
      
      if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
        const canvasContext = canvasElement.getContext('2d', { willReadFrequently: true })
        
        canvasElement.height = videoElement.videoHeight
        canvasElement.width = videoElement.videoWidth
        canvasContext.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height)
        
        const imageData = canvasContext.getImageData(0, 0, canvasElement.width, canvasElement.height)
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height)
        
        if (qrCode) {
          console.log('scan -> qrCode', qrCode)
          const url = qrCode.data
          
          if (url) {
            try {
              const parsedUrl = new URL(url)
              console.log(parsedUrl)
              
              const allowedDomains = tourSettings.domains
              allowedDomains.map(domain => domain.replace('dev.tourio.com', 'tourio.com'))
              console.log(allowedDomains)
              console.log(parsedUrl.host)
              console.log(allowedDomains.includes(parsedUrl.host))
              
              if (allowedDomains.includes(parsedUrl.host) && parsedUrl.pathname.includes('/qr/')) {
                // Navigate and stop scanning
                stopScanning()
                console.log('redirect to ' + parsedUrl.href)
                console.log('pathname ' + parsedUrl.pathname)
                
                const path = `${parsedUrl.pathname}`
                
                console.log('QrCodeScanner navigate ' + path)
                router.push(path)
                return
              } else {
                stopScanning()
                console.log('Scanned QR code is not a tourio QR code.')
                errorMessage.value = 'camera.scan_result_wrong_domain'
                return
              }
            } catch (error) {
              console.log('Invalid QR code scanned.')
              errorMessage.value = 'camera.scan_result_qr_error'
              console.error(error)
              stopScanning()
            }
          }
        }
      }
      
      if (isScanning.value) {
        animationFrameId = requestAnimationFrame(scan)
      }
    }
    
    // Start video stream
    const startVideo = async () => {
      try {
        // Wait for next tick to ensure DOM elements are rendered
        await nextTick()
        
        const videoElement = videoRef.value
        const canvasElement = canvasRef.value
        
        // Check if video element exists
        if (!videoElement || !canvasElement) {
          console.error('Video or canvas element not found, retrying in 100ms')
          // Retry after a short delay
          setTimeout(() => {
            if (isScanning.value) {
              startVideo()
            }
          }, 100)
          return
        }
        
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        })
        
        videoElement.srcObject = stream
        videoElement.setAttribute('playsinline', true) // Required for iOS devices
        await videoElement.play()
        
        animationFrameId = requestAnimationFrame(scan)
      } catch (error) {
        errorMessage.value = 'camera.camera_permission_error'
        console.error('Camera access error:', error)
        stopScanning()
      }
    }
    
    // Start scanning
    const startScanning = () => {
      store.dispatch('pointNavigation/setShowPointNavigation', false)
      errorMessage.value = ''
      isScanning.value = true
    }
    
    // Stop scanning
    const stopScanning = () => {
      store.dispatch('pointNavigation/setShowPointNavigation', true)
      isScanning.value = false
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
        stream = null
      }
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }
    
    // Check permissions and initialize
    const checkPermissionsAndStart = async () => {
      console.log('checkPermissionsAndStart')
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' })
        console.log(permissionStatus.state)
        
        if (permissionStatus.state === 'granted') {
          // startScanning() // Uncomment if you want to auto-start
        }
        
        if (permissionStatus.state === 'denied') {
          errorMessage.value = 'camera.camera_permission_error'
        }
        
        isInitiated.value = true
      } catch (error) {
        console.error('Error checking permissions:', error) // This happens on iOS because navigator.permissions.query is not supported
        isInitiated.value = true
      }
    }
    
    // Watch for scanning state changes
    watch([isScanning, isInitiated], ([newIsScanning, newIsInitiated]) => {
      if (newIsInitiated && newIsScanning) {
        startVideo()
      }
    })
    
    // Component lifecycle
    onMounted(() => {
      checkPermissionsAndStart()
    })
    
    onUnmounted(() => {
      stopScanning()
    })
    
    return {
      isInitiated,
      isScanning,
      errorMessage,
      videoRef,
      canvasRef,
      tourSettings,
      startScanning,
      stopScanning
    }
  }
}
</script>

<style scoped>
/* All styling now handled by Tailwind classes */
</style> 